import { Controller, useWatch } from "react-hook-form";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useEffect, useMemo } from "react";
import HtmlRichTextEditor from "@/components/RichTextEditor/HtmlRichTextEditor";

const EXECUTIVE_SEARCH_BANNER_URL =
  "https://lirp.cdn-website.com/a3d50241/dms3rep/multi/opt/ExecuSearch+Color+Logo-180w.png";

const generateTemplate = (
  facilityName: string,
  jobTitle: string,
  consultantName: string,
  consultantEmail: string,
  applicationUrl: string,
  includeImage: boolean
) => {
  // Wrap content in HTML so Lexical recognises links automatically
  const mainCopy = `<p style="text-align:center;">${facilityName} has engaged the PGA of America's PGA ExecuSearch service to identify and recruit candidates for consideration for the club's ${jobTitle} position. For information and details regarding the position, please refer to the recruitment site below. All questions for this should be directed to the consultant of this recruitment, ${consultantName}, PGA (<a href="mailto:${consultantEmail}">${consultantEmail}</a>).</p>`;

  const linkText = `${jobTitle} | ${facilityName}`;

  const linkLine = `<p style="text-align:center;">${
    applicationUrl ? `<a href="${applicationUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>` : linkText
  }</p>`;

  // Center the ExecuSearch banner using a wrapping <p> tag with text-align center so it still renders nicely
  const banner = includeImage
    ? `\n\n<p style="text-align:center; margin-top:24px;"><img src="${EXECUTIVE_SEARCH_BANNER_URL}" alt="PGA ExecuSearch Banner" style="max-width:180px;" /></p>`
    : "";

  return `${mainCopy}${linkLine}${banner}`;
};

export function JobDescriptionStep({ form }: { form: any }) {
  const {
    control,
    formState: { errors },
    setValue
  } = form;

  const { user } = useCurrentUser();
  const serviceLevel = useWatch({ control, name: "serviceLevel" });
  const facility = useWatch({ control, name: "facility" });
  const jobTitle = useWatch({ control, name: "name" });
  const applicationUrl = useWatch({ control, name: "externalJobUrl" });

  const isAdvancedOrExecutive = serviceLevel === "Advanced" || serviceLevel === "Executive";

  const overview = useWatch({ control, name: "overview" });

  const template = useMemo(() => {
    if (isAdvancedOrExecutive && user && facility && jobTitle) {
      return generateTemplate(
        facility,
        jobTitle,
        user.name || "[Consultant Name]",
        user.email || "[Consultant Email]",
        applicationUrl || "",
        isAdvancedOrExecutive
      );
    }
    return "";
  }, [isAdvancedOrExecutive, user, facility, jobTitle, applicationUrl]);

  // These are the blocks Lexical automatically inserts when navigating to overview. In this state,
  // it is technically "empty" to the user, so should be over-written when present.
  const TEXT_PLACEHOLDER = "<p><br></p>";

  useEffect(() => {
    if (isAdvancedOrExecutive && (overview === "" || overview === template || overview === TEXT_PLACEHOLDER)) {
      setValue("overview", template);
    }
  }, [template, isAdvancedOrExecutive, overview, setValue, TEXT_PLACEHOLDER]);

  return (
    <div className="space-y-6">
      <div className="text-headline-small">Enter the overview description for the job.</div>

      <div className="col-span-full">
        <Controller
          name="overview"
          control={control}
          rules={{ required: "Overview is required" }}
          render={({ field: { onChange, value } }) => {
            return (
              <HtmlRichTextEditor
                label="Job Overview"
                placeholder="Describe the job responsibilities, duties, and activities"
                initialContent={value}
                onChange={(newContent) => {
                  onChange(newContent);
                }}
                error={errors.overview?.message?.toString()}
                enableHtmlFeatures={true}
                enableImageUpload={isAdvancedOrExecutive}
              />
            );
          }}
        />
      </div>
    </div>
  );
}
